import type { HelpArticle } from "../../types";
import { W3_RESULTS } from "../../../calculations/calcPreviewFixtures";

const pct = (value: number): string => `${(value * 100).toFixed(1)}%`;
const num = (value: number): string => value.toLocaleString(undefined, { maximumFractionDigits: 3 });

export const memberResultsArticle: HelpArticle = {
  id: "member-results",
  title: "Reading wood member results",
  description: "Read the engine result from governing verdict through check cards, reactions, response diagrams, factor ledger, combinations, and provenance.",
  tags: ["wood", "results", "DCR", "reaction", "diagram", "factor ledger", "provenance"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "The right pane is a stored engine result, arranged from verdict to trace. The summary answers what governs; reactions and diagrams show the response; the ledger and detail accordions show how the engine priced it. No structural demand, capacity, factor, or labeled diagram maximum is calculated in the browser.",
    },

    { kind: "heading", level: 2, text: "First identify the result state", id: "state" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", W3_RESULTS.mark, "Results"] },
    {
      kind: "paragraph",
      text: "The result surface has four honest terminal states. An incomplete sheet lists the inputs still required. An unsupported sheet says Engine support pending and gives the adapter's reasons without numbers. An engine error prints the failure state. Only an OK result renders the provenance banner, verdict, checks, reactions, diagrams, factor ledger, and detailed tables.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Edited inputs make the displayed result stale",
      body: "Free-text edits remain an editing state until they commit and recompute. A linked upstream change can mark the sheet OUT OF DATE. Read the workspace status before treating the stored result as current; the presence of old cards is not a substitute for a current compute.",
    },

    { kind: "heading", level: 2, text: "Summary: section, checks, reactions", id: "summary" },
    {
      kind: "paragraph",
      text: "The summary begins with the resolved section and one card per returned check. Each card pairs utilization with demand over capacity, the governing combination, and the engine reference for that limit state. The header strip repeats compact DCR chips so the governing state remains visible while you work in the input column.",
    },
    {
      kind: "shot",
      shotId: "wood-results-summary",
      caption: "B-4's summary holds the computed section, six check cards, and two support reaction rows in one review surface.",
    },
    {
      kind: "table",
      caption: `${W3_RESULTS.mark}: exact summary rows imported from the same fixture as the capture.`,
      headers: ["Check", "Demand / capacity", "DCR", "Combination"],
      rows: W3_RESULTS.checks.map((check) => [
        check.label,
        `${num(check.demand)} / ${num(check.capacity)} ${check.units}`,
        pct(check.utilization),
        check.combo,
      ]),
    },
    {
      kind: "paragraph",
      text: `B-4 passes with ${W3_RESULTS.checks[0].label.toLowerCase()} governing at ${pct(W3_RESULTS.checks[0].utilization)}: ${num(W3_RESULTS.checks[0].demand)} lb-ft demand over ${num(W3_RESULTS.checks[0].capacity)} lb-ft capacity under ${W3_RESULTS.checks[0].combo}. The verdict describes this fixture only; it is not a reusable capacity for every ${W3_RESULTS.section} member.`,
    },
    {
      kind: "paragraph",
      text: "Below the cards, one row per support preserves the returned reaction and minimum bearing. Reaction is an engine envelope for display; when a reaction packet is available, its separate per-load-type currency is what a downstream load link consumes.",
    },
    {
      kind: "values",
      caption: `${W3_RESULTS.mark}: reaction rows.`,
      rows: W3_RESULTS.reactions.map((reaction) => ({
        label: reaction.label,
        value: `${num(reaction.reactionLb)} lb · ${reaction.minBearingIn.toFixed(3)} in minimum bearing · D ${num(reaction.byType.D)} lb · L ${num(reaction.byType.L)} lb`,
      })),
    },

    { kind: "heading", level: 2, text: "Response diagrams use the returned model", id: "diagrams" },
    {
      kind: "paragraph",
      text: "The diagram set follows the active role and the data the result published. A simple uniformly loaded beam can render the engine's combination maxima through the simple-span view. A general beam with multiple supports, overhangs, point loads, or partial loads uses the engine's finite-element geometry and station samples when provided. The UI does not draw an analytic curve and label it as a sampled engine response.",
    },
    {
      kind: "shot",
      shotId: "wood-results-diagrams",
      caption: "B-4's response panel presents shear, moment, deflection, and bearing from the same D+L result model.",
    },
    {
      kind: "paragraph",
      text: "At wide layouts the major response figures appear as a grid with the governing combination named in the toolbar. The tab strip remains the compact alternative. Changing the diagram combination changes the displayed response case; it does not rewrite the governing check or recompute the member.",
    },

    { kind: "heading", level: 2, text: "Factor ledger and adjusted values", id: "ledger" },
    {
      kind: "paragraph",
      text: "Detailed mode places the adjustment-factor ledger below the response figures. Every visible chip shows the engine-returned symbol and value. Applicability is printed only when duplicate symbols need to be distinguished; basis and reference are joined into the chip's browser title rather than rendered as ledger text. Factor kind is not rendered. Adjusted reference values underneath are returned alongside that ledger. The browser neither fills missing factors with 1.0 nor derives a primed stress from the input toggles.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "An input and its factor are not the same evidence",
      body: "Card C proves what was declared; the result ledger proves what the engine resolved. Review both. Whether the declared service, repetitive-member, bracing, or orientation condition is applicable remains a project engineering judgment.",
    },

    { kind: "heading", level: 2, text: "Detailed checks and combination tables", id: "details" },
    {
      kind: "paragraph",
      text: "Limit-state detail accordions retain the equation label, substituted demand and capacity, DCR, verdict, governing combination, reference, and engine intermediates available for each check. The combination tables expand the result beyond the one governing row: each combination carries its method, duration or lambda factor when applicable, demand, capacity, utilization, verdict, and stability data where returned.",
    },
    {
      kind: "shot",
      shotId: "wood-results-details",
      caption: "The detailed result begins with the complete factor stack and the adjusted reference values returned beside it.",
    },
    {
      kind: "paragraph",
      text: "Reference capacities identify the section source that ran: catalog member key or typed basis, section geometry, reference stresses, supplement table, and source revision when present. Assumptions and warnings are printed as engine text. Calculation provenance records the calculator, method, and runtime edition carried by the result. These fields make a result auditable without turning the help article into a substitute calculation.",
    },

    { kind: "heading", level: 2, text: "A reliable review order", id: "review-order" },
    {
      kind: "steps",
      steps: [
        { title: "Check currency", body: "Confirm the sheet is saved, not editing, not stale from a linked source, and not in an incomplete or unsupported state." },
        { title: "Confirm the computed section", body: "Compare the result's section identity and provenance with card A. A section edit that has not recomputed must not be mistaken for the stored result." },
        { title: "Read governing, then all checks", body: "Start at the largest DCR, but scan every returned limit state and its units, combination, and reference." },
        { title: "Trace response and supports", body: "Compare diagrams, reaction rows, support geometry, and minimum bearing with the intended load path and detailing." },
        { title: "Audit the basis", body: "Review the factor ledger, adjusted values, detailed combinations, reference source, warnings, assumptions, and runtime provenance." },
      ],
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Result interpretation is an engineering boundary",
      body: "Firmark reports the engine's checks and trace. Accepting the member, criteria, support idealization, combinations, minimum bearing, bracing, detailing, and omissions requires responsible engineering review. Do not infer approval from a green verdict alone.",
    },

    { kind: "related", topics: ["member-geometry-supports", "member-loads", "design-conditions", "member-picker", "linking-reactions"] },
  ],
};
